/**
 * Pure helpers for the Alert Center (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-I).
 * The server groups, filters and counts; these only shape what the page shows.
 */

export type Severity = 'critical' | 'warning' | 'info';
export type AlertStatus = 'new' | 'acknowledged' | 'resolved' | 'ignored';
export type View = 'needs_attention' | 'critical' | 'mine' | 'open' | 'resolved' | 'all';

export interface AlertItem {
  id: string;
  source: string;
  source_id: string;
  area: string;
  severity: Severity;
  title: string;
  message: string;
  created_at: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  occurrences: number;
  member_ids: string[];
  status: AlertStatus;
  native_status: AlertStatus;
  alert_type: string;
  link: string;
  details: unknown;
  assignee_admin_id: string | null;
  assignee_email: string | null;
  note: string | null;
  state_updated_at: string | null;
  state_updated_by: string | null;
}

export interface SourceHealth {
  key: string;
  label: string;
  area: string;
  note: string;
  ok: boolean;
  error: string | null;
  rows: number;
  last_written_at: string | null;
}

export interface AlertCounts {
  needs_attention: number;
  critical_open: number;
  mine_open: number;
  by_status: Record<AlertStatus, number>;
  open_by_area: Record<string, number>;
  open_by_severity: Record<Severity, number>;
}

export interface AlertsResponse {
  alerts: AlertItem[];
  total: number;
  truncated: boolean;
  counts: AlertCounts;
  sources: SourceHealth[];
  areas: { key: string; label: string }[];
  views: View[];
  since_days: number;
}

export interface Filters {
  view: View;
  area?: string;
  source?: string;
  severity?: string;
  status?: string;
  assignee?: string;
  q?: string;
  since_days?: number;
}

export const VIEW_LABELS: Record<View, string> = {
  needs_attention: 'Needs attention',
  critical: 'Critical',
  mine: 'Assigned to me',
  open: 'All open',
  resolved: 'Resolved',
  all: 'All',
};

export const STATUS_LABELS: Record<AlertStatus, string> = {
  new: 'New',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
  ignored: 'Ignored',
};

/** Tailwind classes per severity: [pill background+text, left border]. */
export const SEVERITY_STYLE: Record<Severity, { pill: string; border: string }> = {
  critical: { pill: 'bg-red-100 text-red-800', border: 'border-l-red-500' },
  warning: { pill: 'bg-amber-100 text-amber-800', border: 'border-l-amber-400' },
  info: { pill: 'bg-gray-100 text-gray-700', border: 'border-l-gray-300' },
};

/** Query string for GET /api/admin/alerts (empty filters dropped). */
export function buildQuery(f: Filters): string {
  const params = new URLSearchParams();
  params.set('view', f.view);
  (['area', 'source', 'severity', 'status', 'assignee', 'q'] as const).forEach(k => {
    const v = f[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') params.set(k, String(v).trim());
  });
  if (f.since_days) params.set('since_days', String(f.since_days));
  return params.toString();
}

/** Parse filters from the page URL so a filtered view can be bookmarked. */
export function filtersFromSearch(search: string): Filters {
  const p = new URLSearchParams(search);
  const view = (p.get('view') as View) || 'needs_attention';
  const days = parseInt(p.get('since_days') || '', 10);
  return {
    view: VIEW_LABELS[view] ? view : 'needs_attention',
    area: p.get('area') || undefined,
    source: p.get('source') || undefined,
    severity: p.get('severity') || undefined,
    status: p.get('status') || undefined,
    assignee: p.get('assignee') || undefined,
    q: p.get('q') || undefined,
    since_days: Number.isFinite(days) ? days : undefined,
  };
}

/** Every underlying alert id for the selected (grouped) rows, de-duplicated. */
export function memberIds(rows: AlertItem[], selected: Set<string>): string[] {
  const out = new Set<string>();
  rows.forEach(r => {
    if (!selected.has(r.id)) return;
    (r.member_ids && r.member_ids.length ? r.member_ids : [r.id]).forEach(id => out.add(id));
  });
  return Array.from(out);
}

/** "3 min ago", "5 h ago", "12 days ago". */
export function ago(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

/**
 * A source is "quiet" when its newest row is older than `days` — for most
 * sources that just means nothing went wrong, but a writer that silently died
 * looks exactly like this, so the page says so instead of showing "0".
 */
export function isQuiet(source: SourceHealth, days = 14, now: number = Date.now()): boolean {
  if (!source.ok) return false; // failing is its own state, shown separately
  if (!source.last_written_at) return true; // never written
  const t = new Date(source.last_written_at).getTime();
  return Number.isFinite(t) && now - t > days * 86_400_000;
}
